import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "../components/layout";
import { useNavigate } from "react-router-dom";
import { UserPlus } from "lucide-react";
import api from "../services/api";
import { getAssetUrl, Pagination } from "../services/helper";

const MentorCard = ({ mentor, onRequest }) => {
  const navigate = useNavigate();
  const [requested, setRequested] = useState(false);

  const handleRequest = () => {
    onRequest(mentor.id);
    setRequested(true);
  };

  // console.og({mentor});
  return (
    <div className="bg-white rounded-[30px] border border-gray-100 shadow-sm p-5 flex flex-col gap-3">
      {/* Top row */}
      <div className="flex items-center gap-3">
        <img
          src={getAssetUrl(mentor.avatar)

          }
          alt={mentor.name}
          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[15px] font-bold text-[#0F172A] font-body">{mentor.name}</p>
            <span className="text-[12.4px] px-4 py-0.5 rounded-full bg-[#F1F5F9] text-[#657786] font-body">
              {mentor.user_role || "Mentor"}
            </span>
          </div>
          <p className="text-[12px] text-[#94A3B8] font-body">Suggested</p>
        </div>
      </div>

      {/* Bio */}
      <p className="text-[16px] text-[#475569] leading-[1.6] font-body line-clamp-3">
        {mentor.about_us || "Experienced mentor helping others grow in their faith journey."}
      </p>

      {/* Interests / Tags */}
      <div className="flex flex-wrap gap-2">
        {(mentor.interests ? JSON.parse(mentor.interests) : ["Prayer", "Bible Study"]).map((tag, i) => (
          <span
            key={i}
            className="text-[15px] text-[#475569] font-body rounded-full px-3 py-1 bg-gray-100"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Buttons */}
      <div className="flex gap-2 mt-1">
        <button
          onClick={handleRequest}
          disabled={requested || mentor.is_connected == 1}
          className={`flex-1 py-2.5 rounded-lg text-[13px] font-semibold font-body transition-all flex items-center justify-center gap-2
    ${requested || mentor.is_connected === 1
              ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-70"
              : "bg-[#1F3A5F] text-white hover:bg-[#16304f] cursor-pointer"
            }`}

        >
           {mentor.is_connected==1 ?  `(Already Connected)` :requested ? "✓ Request Sent" : "Request Mentorship" }
        </button>

        <button
          onClick={() => navigate(`/other-profile/${mentor.id}`)}
          className="px-4 py-2.5 rounded-lg text-[13px] font-semibold font-body text-[#1F3A5F] border border-[#E2E8F0] hover:bg-gray-50 transition-all"
        >
          View Profile
        </button>
      </div>
    </div>
  );
};

const Mentorship = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  // Fetch Suggested Mentors
  const { data, isLoading, error } = useQuery({
    queryKey: ["suggested_mentors", page],
    queryFn: () => api.getSuggestedMentors(page),
    staleTime: 5 * 60 * 1000,
  });

  const mentors = data?.data || [];
  const pagination = data?.pagination || { page: 1, totalPages: 1, hasNext: false };

  // Request Mentorship Mutation
  const requestMutation = useMutation({
    mutationFn: (mentorId) => api.MentorshipRequest(mentorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suggested_mentors", 1] });
    },
  });

  const handleRequestMentorship = (mentorId) => {
    requestMutation.mutate(mentorId);
  };

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-56px)] w-full overflow-y-auto 
                [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden 
                py-4 sm:py-6 px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 2xl:px-16">
        {/* Title - Left Aligned */}
        <div className="mb-6">
          <h1 className="text-[28px] sm:text-[32px] font-bold text-[#0F172A] font-body text-left">
            Mentorship
          </h1>
        </div>

        {/* Loading State - Centered */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className="w-12 h-12 border-4 border-[#1F3A5F] border-t-transparent rounded-full animate-spin mb-6" />
            <p className="text-[#64748B] text-lg font-medium">Finding best mentors for you...</p>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center min-h-[50vh]">
            <p className="text-red-500 text-lg font-medium">Failed to load mentors. Please try again later.</p>
          </div>
        ) : mentors.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {mentors.map((mentor) => (
                <MentorCard
                  key={mentor.id}
                  mentor={mentor}
                  onRequest={handleRequestMentorship}
                />
              ))}
            </div>

            <div className="mt-10 flex justify-center">
              <Pagination pagination={pagination} onPageChange={setPage} />
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="bg-white border border-gray-100 rounded-[30px] shadow-sm px-10 py-12 max-w-lg w-full text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-[#EEF4FF] flex items-center justify-center mb-5">
                <UserPlus className="w-8 h-8 text-[#1F3A5F]" />
              </div>
              <h2 className="text-2xl font-bold text-[#0F172A] font-body">No Mentors Found</h2>
              <p className="mt-3 text-[#64748B] text-[15px] leading-6 font-body">
                There are no suggested mentors available at the moment.<br />
                Please check back later.
              </p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Mentorship;